import { Box, Button, Tooltip, Typography } from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  governanceElements,
  miniSidebarElements,
  sidebarElements1,
  sidebarElementsGroupAdmin,
  sidebarElementsFacility,
} from "../utils/constant";
import { useUser } from "../context/AuthContext";
import NirantaraIcon from "../assets/images/vajra.svg";
import { getConsultantIconUrl } from "../utils/consultantLogo";
import WestIcon from "@mui/icons-material/West";
import CheckIcon from "@mui/icons-material/Check";
import SidebarItemsRenderer from "../utils/layoutFunctions";
import {
  filterMiniSidebarElements,
  getRoleBasedSidebar,
} from "../utils/sidebarFunctions";
import socialDataSidebarStyles from "../styles/layout/socialDataSidebar.module.css";
import { get } from "../utils/api";

const GovernanceLayout = () => {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [moduleFromData, setModuleFromData] = useState("");
  const [completionStatus, setCompletionStatus] = useState({});

  // Canonical module slugs <-> existing route slugs (backward compatible)
  const moduleRouteSlugMap = {
    "compliance-ethics": "compliance-ethics",
    "transparency-disclosure": "transparancy-disclosure",
    policies: "policy-form",
  };

  const canonicalSlugFromRoute = (slug) => {
    if (slug === "compliance-ethics") return "compliance-ethics";
    if (slug === "compliance-form") return "compliance-ethics";
    if (slug === "transparancy-disclosure") return "transparency-disclosure";
    if (slug === "policy-form") return "policies";
    return slug;
  };

  const routeSlugFromCanonical = (slug) => moduleRouteSlugMap[slug] || slug;

  const sidebarElements = getRoleBasedSidebar({
    user,
    sidebarElements1,
    sidebarElementsFacility,
  });

  const filteredSidebar = sidebarElements.map((item) => {
    if (item.path === "/production-revenue") {
      return {
        ...item,
        heading: user?.companyAdmin ? "Production & Revenue" : "Production",
      };
    }
    if (item.heading === "Setup" && item.subData) {
      const newSubData = item.subData.filter(
        (sub) => sub.primary !== "Facility Master" || !user?.facility,
      );
      return { ...item, subData: newSubData };
    }
    return item;
  });

  const miniSidebarFiltered = filterMiniSidebarElements({
    user,
    miniSidebarElements,
  });

  // Governance data modules for the vertical sidebar
  const governanceDataModules = [
    { label: "Board Structure and Composition", slug: "board-structure" },
    { label: "Executive Compensation", slug: "executive-compensation" },
    { label: "Risk Management", slug: "risk-management" },
    { label: "Compliance and Ethics", slug: "compliance-ethics" },
    { label: "Shareholder Rights", slug: "shareholder-rights" },
    { label: "Transparency and Disclosure", slug: "transparency-disclosure" },
    { label: "Policies", slug: "policies" },
  ];

  // Parse governance data routes: /governance/data/<slug>/<id>
  const getGovernanceDataRouteInfo = () => {
    const parts = location.pathname.split("/").filter(Boolean);
    const govIdx = parts.indexOf("governance");
    const dataIdx = parts.indexOf("data");
    if (govIdx === -1 || dataIdx === -1)
      return { activeSlug: "", id: "", isFormPage: false, module: "" };

    const rawSlug = parts[dataIdx + 1] || "";
    const slug = canonicalSlugFromRoute(rawSlug);
    const id = parts[dataIdx + 2] || "";

    // Do NOT read module from URL. Always rely on backend record for the current id.
    const module = (moduleFromData || "").toLowerCase().trim();

    const isFormPage =
      !!slug && governanceDataModules.some((m) => m.slug === slug);

    return { activeSlug: slug, id, isFormPage, module };
  };

  const checkModuleCompletion = useCallback(
    async (id) => {
      try {
        const response = await get(`/governance/getGovernance`, {
          params: { id },
        });
        const governanceData = response?.data?.data?.[0];

        // module can be stored as "quarterly" / "annual"
        const rawModule = (
          governanceData?.module ||
          governanceData?._governanceDataRef?.module ||
          ""
        )
          .toLowerCase()
          .trim();
        let normalizedModule = "";
        if (rawModule.includes("quarter")) normalizedModule = "quarterly";
        else if (rawModule.includes("annual") || rawModule.includes("month"))
          normalizedModule = "annual";
        setModuleFromData(normalizedModule);

        // Determine completion (used to disable future modules) via backend flags.
        // Some forms store stepsCompleted inside each module payload, so read both.
        const steps = governanceData?.stepsCompleted || {};
        const nestedSteps = {
          boardStructure:
            governanceData?.boardStructure?.stepsCompleted?.boardStructure,
          executiveCompensation:
            governanceData?.executiveCompensation?.stepsCompleted
              ?.executiveCompensation,
          riskManagement:
            governanceData?.riskManagement?.stepsCompleted?.riskManagement,
          complianceAndEthics:
            governanceData?.complianceAndEthics?.stepsCompleted
              ?.complianceAndEthics,
          shareholderRights:
            governanceData?.shareholderRights?.stepsCompleted
              ?.shareholderRights,
          transparencyDisclosure:
            governanceData?.transparencyDisclosure?.stepsCompleted
              ?.transparencyDisclosure ??
            governanceData?.transparencyAndDisclosure?.stepsCompleted
              ?.transparencyAndDisclosure ??
            governanceData?.transparencyAndDisclosure?.stepsCompleted
              ?.transparencyDisclosure,
          policies: governanceData?.policies?.stepsCompleted?.policies,
        };
        const complianceObjects = [
          governanceData?.complianceAndEthics,
          governanceData?.complianceEthics,
          governanceData?.compliance_form,
          governanceData?.complianceForm,
        ];
        const hasComplianceNestedStep = complianceObjects.some((obj) => {
          const nested = obj?.stepsCompleted || {};
          return (
            !!nested.complianceAndEthics ||
            !!nested.complianceEthics ||
            Object.values(nested).some(Boolean)
          );
        });
        const isComplianceComplete =
          !!steps.complianceEthics ||
          !!steps.complianceAndEthics ||
          !!steps.compliance_form ||
          !!steps.complianceForm ||
          !!steps["compliance-ethics"] ||
          !!steps["compliance-form"] ||
          !!nestedSteps.complianceAndEthics ||
          hasComplianceNestedStep;
        const isShareholderComplete =
          !!steps.shareholderRights ||
          !!steps["shareholder-rights"] ||
          !!steps.shareholder_rights ||
          !!governanceData?.shareholderRights?.stepsCompleted
            ?.shareholderRights ||
          !!governanceData?.shareholderRights?.stepsCompleted
            ?.shareholder_rights ||
          !!governanceData?.shareholderRights?.stepsCompleted
            ?.shareholderRightsPolicy ||
          !!nestedSteps.shareholderRights;
        setCompletionStatus({
          "board-structure":
            !!steps.boardStructure || !!nestedSteps.boardStructure,
          "executive-compensation":
            !!steps.executiveCompensation ||
            !!nestedSteps.executiveCompensation,
          "risk-management":
            !!steps.riskManagement || !!nestedSteps.riskManagement,
          "compliance-ethics": isComplianceComplete,
          "shareholder-rights": isShareholderComplete,
          "transparency-disclosure":
            !!steps.transparencyDisclosure ||
            !!steps.transparencyAndDisclosure ||
            !!steps.transparencyDisclosureAndReporting ||
            !!steps["transparency-and-disclosure"] ||
            !!nestedSteps.transparencyDisclosure,
          policies: !!steps.policies || !!nestedSteps.policies,
        });
      } catch (error) {
        console.error("Error fetching governance module:", error);
        setModuleFromData("");
        setCompletionStatus({});
      }
    },
    [setModuleFromData],
  );

  // Same pattern as Social: whenever route changes, derive id and fetch module from backend
  useEffect(() => {
    const { id, isFormPage } = getGovernanceDataRouteInfo();
    if (!isFormPage || !id) {
      setModuleFromData("");
      return;
    }
    checkModuleCompletion(id);
  }, [location.pathname, checkModuleCompletion]);
  const renderMainSidebarItems = (elements) => {
    return elements.map((item, index) => (
      <div
        key={index}
        style={{
          fontFamily: "Inter",
          display: "flex",
          flexDirection: "column",
          marginTop: "0.25rem",
        }}
      >
        <div
          key={index}
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor:
              item.primary === "Governance"
                ? "rgba(28, 72, 107, 1)"
                : "transparent",
          }}
        >
          <Tooltip
            title={item.title}
            arrow
            placement="bottom-start"
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 0],
                  },
                },
              ],
            }}
            componentsProps={{
              tooltip: {
                sx: {
                  color: "#fff",
                  fontSize: "12px",
                  borderRadius: "4px",
                  padding: "0.25rem 0.5rem",
                },
                arrow: {
                  color: "#fff",
                },
              },
            }}
          >
            <a
              href={item.path}
              key={item.primary}
              style={{
                textDecoration: "none",
                color: "green",
                padding: "0.4rem",
                borderRadius: "4px",
              }}
            >
              <Box
                component="img"
                src={item.icon}
                alt={"img"}
                sx={{
                  width: "24px",
                  height: "24px",
                }}
              />
            </a>
          </Tooltip>
        </div>
      </div>
    ));
  };

  const renderSidebarItems = (elements) => {
    return elements.map((section, index) => (
      <div
        key={index}
        style={{
          fontFamily: "Inter",
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginTop: "1.5rem",
        }}
      >
        <p
          style={{
            color: "#fff",
            fontWeight: "700",
            fontSize: "24px",
            padding: "0 2rem 0.5rem 0.5rem",
            marginLeft: "1rem",
            borderBottom: "2px solid #fff",
            display: "inline-block",
          }}
        >
          Governance
        </p>
        {section.subData
          .filter(
            (item) => user?.companyAdmin || item.path !== "/governance/data",
          )
          .map((item) => (
            <div key={item.primary} style={{ display: "flex" }}>
              <div
                style={{
                  width: "8px",
                  height: "auto",
                  backgroundColor: location.pathname.startsWith(item.path)
                    ? "#fff"
                    : "transparent",
                  borderRadius: "0 4px 4px 0",
                }}
              ></div>
              <a
                href={item.path}
                key={item.primary}
                style={{
                  textDecoration: "none",
                  color: "#000",
                  padding: "0.4rem 0.75rem",
                  display: "flex",
                  borderRadius: "4px",
                  margin: "0rem 0.75rem 0 0.75rem",
                  width: "100%",
                  backgroundColor: location.pathname.startsWith(item.path)
                    ? "#fff"
                    : "transparent",
                }}
              >
                <Box
                  component="img"
                  src={item.icon}
                  alt={"img"}
                  sx={{
                    color: "red",
                    mr: "8px",
                    filter: location.pathname.startsWith(item.path)
                      ? "invert(0)"
                      : "invert(1)",
                  }}
                />
                <Typography
                  style={{
                    color: location.pathname.startsWith(item.path)
                      ? "rgba(28, 72, 107, 1)"
                      : "#fff",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "24px",
                  }}
                >
                  {item.primary}
                </Typography>
              </a>
            </div>
          ))}
      </div>
    ));
  };

  const renderGovernanceDataModulesSidebar = () => {
    const { activeSlug, id, module } = getGovernanceDataRouteInfo();

    // Filter modules based on selected entry type (annual / quarterly)
    // Quarterly: hide Executive Compensation
    // Annual:   hide Board Structure and Composition
    // Always filter when module is determined (from URL or data)
    const normalizedModule = (module || "").toLowerCase().trim();

    // Check if module is quarterly (handle variations like "quarterly", "quarterly module", etc.)
    const isQuarterly = normalizedModule.includes("quarterly");

    // Check if module is annual (handle variations like "annual", "annual module", etc.)
    const isAnnual = normalizedModule.includes("annual");

    let visibleModules = governanceDataModules;
    if (isQuarterly) {
      // For quarterly: hide Executive Compensation and Policies
      visibleModules = governanceDataModules.filter(
        (m) => m.slug !== "executive-compensation" && m.slug !== "policies",
      );
    } else if (isAnnual) {
      // For annual: hide Board Structure and Composition and Compliance and Ethics
      visibleModules = governanceDataModules.filter(
        (m) => m.slug !== "board-structure" && m.slug !== "compliance-ethics",
      );
    }
    // If module is not determined, show all modules (fallback)

    const accentColor = "#EB7E0E";
    const activeIndex = visibleModules.findIndex((m) => m.slug === activeSlug);

    const isModuleUnlocked = (idx) => {
      if (idx === 0) return true;
      if (!id) return false;
      for (let i = 0; i < idx; i++) {
        const prevSlug = visibleModules[i]?.slug;
        if (prevSlug && !completionStatus[prevSlug]) return false;
      }
      return true;
    };

    return (
      <div>
        <ul className={socialDataSidebarStyles.sidebarList}>
          {visibleModules.map((item, idx) => {
            const isActive = item.slug === activeSlug;
            const isUnlocked = isModuleUnlocked(idx);
            const isCompleted =
              !isActive &&
              (completionStatus[item.slug] === true ||
                (activeIndex !== -1 && idx < activeIndex));
            // Keep current module enabled even if previous completion flags are delayed
            // Also keep completed modules enabled.
            const baseDisabled =
              !id || (!isUnlocked && !isActive && !isCompleted);
            const isDisabled = baseDisabled;

            return (
              <li
                key={item.slug}
                className={socialDataSidebarStyles.sidebarItemWrapper}
              >
                <div
                  className={[
                    socialDataSidebarStyles.sidebarItemRow,
                    isDisabled
                      ? socialDataSidebarStyles.sidebarItemRowDisabled
                      : "",
                  ].join(" ")}
                  style={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (isDisabled) return;
                    navigate(
                      `/governance/data/${routeSlugFromCanonical(item.slug)}/${id}`,
                    );
                  }}
                  title={
                    !id
                      ? "Create an entry first"
                      : !isUnlocked
                        ? "Please complete previous modules first"
                        : item.label
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    if (isDisabled) return;
                    navigate(
                      `/governance/data/${routeSlugFromCanonical(item.slug)}/${id}`,
                    );
                  }}
                >
                  <div
                    className={[
                      socialDataSidebarStyles.dot,
                      isCompleted
                        ? socialDataSidebarStyles.dotChecked
                        : isActive
                          ? socialDataSidebarStyles.dotActive
                          : socialDataSidebarStyles.dotUpcoming,
                    ].join(" ")}
                    style={{
                      borderColor: accentColor,
                      backgroundColor:
                        isCompleted || isActive ? accentColor : "transparent",
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    {/* {isCompleted && !isActive && (
                      <CheckIcon
                        className={socialDataSidebarStyles.checkIcon}
                        style={{ width: "1rem" }}
                      />
                    )} */}
                  </div>
                  <Typography
                    className={socialDataSidebarStyles.text}
                    sx={{
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? accentColor : "#FFFFFF",
                      fontSize: "12px",
                      opacity: isDisabled ? 0.6 : 1,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.label}
                  </Typography>
                </div>

                {item.slug !==
                  visibleModules[visibleModules.length - 1].slug && (
                  <div
                    className={socialDataSidebarStyles.connector}
                    style={{
                      borderLeftColor: accentColor,
                      opacity: isDisabled ? 0.4 : 1,
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      <Box style={{ display: "flex", overflow: "hidden", width: "100%" }}>
        <div
          style={{
            width: "22.5%",
            height: "100vh",
            backgroundColor: "#E6E6E6",
            position: "relative",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Box display="flex" justifyContent="center" py={1}>
            <img
              src={getConsultantIconUrl() || NirantaraIcon}
              alt="Nirantara Logo"
              style={
                getConsultantIconUrl()
                  ? { cursor: "pointer", height: "36px", width: "36px", objectFit: "contain" }
                  : { cursor: "pointer", height: "44px", width: "auto", objectFit: "contain" }
              }
              onClick={() => navigate("/dashboard")}
            />
          </Box>
          <div style={{ width: "100%", marginTop: "1rem" }}>
            {renderMainSidebarItems(miniSidebarFiltered)}
          </div>

          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "21.3vw",
              height: "100vh",
              backgroundColor: "#fff",
              zIndex: 10,
              overflowY: "auto",
              transform: isHovered ? "translateX(0)" : "translateX(-100%)",
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.4s ease, transform 0.4s ease",
              pointerEvents: isHovered ? "auto" : "none",
              willChange: "transform, opacity",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {
              <SidebarItemsRenderer
                elements={
                  user?.groupManager
                    ? sidebarElementsGroupAdmin
                    : filteredSidebar
                }
              />
            }
          </div>
        </div>
        <div
          style={{ width: "77.5%", backgroundColor: "rgba(28, 72, 107, 1)" }}
        >
          {getGovernanceDataRouteInfo().isFormPage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                px: 3,
                pt: 2,
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate("/governance/data")}
                sx={{
                  textTransform: "none",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: 0,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <WestIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Back
              </Button>
            </Box>
          )}
          {getGovernanceDataRouteInfo().isFormPage
            ? renderGovernanceDataModulesSidebar()
            : renderSidebarItems(governanceElements)}
        </div>
      </Box>
    </>
  );
};

export default GovernanceLayout;
